import { useMutation } from '@tanstack/react-query'
import {
  Alert,
  Button,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import React, { useEffect, useState } from 'react'
import { completeRefund, retryRefundFollowUp } from '../../../../../Actions'
import {
  canCompleteRefund,
  canHoldRefund,
  canRetryFollowUp,
  canWithdrawRefund,
  CompleteRefundRequest,
  CompleteRefundResponse,
  deactivatesSubscription,
  FOLLOW_UP_STATUS_COLOR,
  followUpStatusLabel,
  cancelKindLabel,
  formatRatio,
  PENALTY_BRACKET_LABEL,
  REFUND_CHANNEL_LABEL,
  REFUND_REASON_ACTION,
  REFUND_REASON_FOLLOW_UP,
  REFUND_FOLLOW_UP_ACTION_LABEL,
  REFUND_REASON_LABEL,
  REFUND_STATUS_COLOR,
  REFUND_STATUS_LABEL,
  RefundChannel,
  RefundFollowUp,
  RefundLedgerRow,
  RefundReason,
  RefundTarget,
  refundTargetLabel,
  refundTargetOfRow,
} from '../../../../../types/refund'
import { apiErrorMessage } from '../../../../../utils/apiError'
import RefundHistory from './RefundHistory'
import RefundStateActions from './RefundStateActions'
import useInvalidateRefunds from './useInvalidateRefunds'

type Props = {
  open: boolean
  row: RefundLedgerRow | null
  workspaceId: number
  /**
   * 이 행을 정정한 기록. 대장이 계산해서 넘긴다 — 서버가 주는 값은 반대 방향
   * (`supersedesRefundId`) 하나뿐이라, "내가 정정됐다" 는 같은 워크스페이스 행들을 훑어야 안다.
   */
  supersededByRefundId?: number | null
  /** 정정 연결을 타고 다른 행으로 옮겨간다. 없으면 링크를 걸지 않는다 */
  onOpenRefund?: (refundId: number) => void
  onClose: () => void
}

const money = (value?: number | null) => (value == null ? '-' : `${Number(value).toLocaleString()}원`)
const datetime = (value?: string | null) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-')

/** BE `CompleteRefundRequest.refundedAt` 은 `LocalDateTime` 이다. 날짜만 보내면 파싱에서 끊긴다 */
const toLocalDateTime = (value: Dayjs) => value.format('YYYY-MM-DD[T]HH:mm:ss')

/** 서버가 400 의 사유를 주지 않았을 때(`utils/apiError`) 대신 규칙을 적어 보여준다 */
const NO_REASON =
  '서버가 요청을 거절했지만 사유 문장을 주지 않았습니다. 처리일이 미래이거나 결제일보다 이르지 않은지, 실제 취소 금액이 0 보다 큰지, 이미 처리 완료된 행이 아닌지 확인하세요.'

/** 확정·재시도 뒤 후속 조치가 어떻게 끝났나. 서랍을 닫을지가 여기서 갈린다 */
type FollowUpOutcome = 'OK' | 'PENDING' | 'FAILED'

/**
 * 확정 폼의 문구는 **환불 유형에 따라 갈린다.**
 *
 * 🔴 잔류형(전환 청약철회)은 직전 약정을 복원만 하고 구독은 계속되는데, 종전에는 유형과 무관하게
 * "구독 비활성 진입일이 됩니다"·"구독 비활성 전환을 발화시킵니다" 를 띄웠다. 담당자가 구독이
 * 끊길까 봐 확정을 망설였다(환불 QA 회차 2026-09-21~22 · 화면 구성 v3 S8).
 *
 * 처리일이 **비활성 진입일**이 되는 것은 구독이 꺼지는 유형뿐이다. 그 유형에서만 진입일이 단건
 * 무위약금 선택권 30일과 상태 경계의 기산점이 되므로, 라벨이 그 무게를 져야 하는 것도 그때뿐이다.
 */
const DO_NOT_PRESS = '돈이 아직 나가지 않았다면 누르지 마세요.'

const confirmCopy = (reason: RefundReason, target: RefundTarget | null) => {
  // 🔴 크레딧 팩 결제 건은 사유와 무관하게 남은 크레딧 소멸이다(D-124). 사유 표(`REFUND_REASON_ACTION`)
  // 로 읽으면 "후속 조치 없음" 이라 적어 놓고 확정이 크레딧을 없앤다
  if (target?.kind === 'PAYMENT_ORDER') {
    return {
      refundedAtHint: '결제대행사(단건 상점)에서 실제로 취소한 날입니다',
      warning: `확정하면 후속 조치가 실행됩니다 — ${REFUND_FOLLOW_UP_ACTION_LABEL.REVOKE_CREDIT_PACK}. 부분 환불이어도 남은 크레딧은 전부 없어지며 되돌릴 수 없습니다. 구독은 그대로입니다. ${DO_NOT_PRESS}`,
    }
  }

  const followUp = REFUND_REASON_FOLLOW_UP[reason] ?? '후속 조치 없음'
  // 조치 이름을 문장 안에 넣지 않고 줄표 뒤에 세운다 — 이름마다 조사가 갈려("전환을",
  // "종료를") 템플릿에 붙이면 어느 한쪽이 반드시 틀린다
  const fires = `확정하면 후속 조치가 실행됩니다 — ${followUp}.`

  if (deactivatesSubscription(reason)) {
    return {
      refundedAtHint: '구독 비활성 진입일이 됩니다',
      warning: `${fires} 처리일이 비활성 진입일이 되고 단건 무위약금 선택권 30일의 기산점이 됩니다. ${DO_NOT_PRESS}`,
    }
  }
  if (REFUND_REASON_ACTION[reason] === 'NONE') {
    return {
      refundedAtHint: '결제대행사에서 실제로 취소한 날입니다',
      warning: `이 유형은 후속 조치가 없습니다. 실행 결과만 기록되고 구독은 그대로입니다. ${DO_NOT_PRESS}`,
    }
  }
  return {
    refundedAtHint: '결제대행사에서 실제로 취소한 날입니다',
    warning: `${fires} 구독은 유지되며 비활성 전환은 없습니다. ${DO_NOT_PRESS}`,
  }
}

const channelOptions = (Object.keys(REFUND_CHANNEL_LABEL) as RefundChannel[]).map((value) => ({
  value,
  label: REFUND_CHANNEL_LABEL[value],
}))

const credit = (value?: number | null) => (value == null ? '-' : `${Number(value).toLocaleString()}크레딧`)

/**
 * 크레딧 팩 소멸량 (D-124 · aivatar #540). 미리보기면 없앨, 확정 결과면 없앤 크레딧이다.
 *
 * 🔴 불일치는 **서버 판정(`revocationMismatch`)만** 본다. 접수 사용률이 반올림돼 저장되므로 화면이
 * `revokedCredit` 과 `unusedAtIntake` 를 직접 비교하면 같은 건을 불일치로 읽는다.
 */
function RevocationSummary({ followUp }: { followUp: RefundFollowUp }) {
  const revoked = followUp.revokedCredit
  return (
    <Space direction="vertical" size={2}>
      {revoked == null ? (
        <span>소멸량을 응답에서 받지 못했습니다.</span>
      ) : Number(revoked) === 0 ? (
        // 이미 닫힘·다 씀·소멸 후 재량 처리. 무언가 없앤 것처럼 읽히면 안 된다 — 서버도 "실행 안 함" 으로 남긴다
        <span>없앨 크레딧이 없습니다 — 후속 조치는 &quot;실행 안 함&quot; 으로 기록됩니다.</span>
      ) : (
        <span>
          {followUp.executed ? '없앤' : '없앨'} 크레딧 <b>{credit(revoked)}</b> (확정 시점 미사용분)
        </span>
      )}
      {followUp.revocationMismatch ? (
        <Alert
          type="warning"
          showIcon
          message="접수 뒤 크레딧 사용이 바뀌었습니다"
          description={`접수 때 미사용분은 ${credit(followUp.unusedAtIntake)}, 확정 시점은 ${credit(revoked)} 입니다. 환불액은 접수 때 산정 그대로이며 소멸은 확정 시점 미사용분 기준입니다.`}
        />
      ) : null}
    </Space>
  )
}

/** 미리보기·확정 결과의 후속 조치 요약. `dryRun` 이면 `executed` 가 항상 거짓이다 */
function FollowUpSummary({ followUp }: { followUp: RefundFollowUp | null }) {
  if (!followUp) return <span>후속 조치 정보가 응답에 없습니다.</span>

  return (
    <Space direction="vertical" size={2}>
      <b>{REFUND_FOLLOW_UP_ACTION_LABEL[followUp.action] ?? followUp.action}</b>
      <span>{followUp.description}</span>
      {followUp.action === 'REVOKE_CREDIT_PACK' ? <RevocationSummary followUp={followUp} /> : null}
      {followUp.details?.map((detail) => (
        <Typography.Text key={detail} type="secondary">
          · {detail}
        </Typography.Text>
      ))}
      {followUp.restoredSubscriptionId ? <span>복원 대상 구독 #{followUp.restoredSubscriptionId}</span> : null}
    </Space>
  )
}

/**
 * 환불 기록 상세 + 처리 완료 확정 (화면 명세 2.1 · aipark-front #266 P3)
 *
 * 🔴 **확정은 되돌릴 수 없는 조치를 발화시킨다** — 구독 즉시 비활성 전환, 직전 약정 복원.
 * 그래서 `dryRun: true` 미리보기를 받기 전에는 확정 버튼을 열지 않는다.
 *
 * 미리보기는 공짜 안전장치이기도 하다. 서버 `preview()` 가 확정과 **똑같은**
 * `validateCompletion()` 을 통과시키므로, 미리보기가 200 이면 같은 본문의 확정도 통과한다.
 */
export default function RefundDetailDrawer({
  open,
  row,
  workspaceId,
  supersededByRefundId,
  onOpenRefund,
  onClose,
}: Props) {
  const [refundedAt, setRefundedAt] = useState<Dayjs | null>(null)
  const [channel, setChannel] = useState<RefundChannel | undefined>()
  const [actualCancelAmount, setActualCancelAmount] = useState<number | null>(null)
  const [pgTransactionNo, setPgTransactionNo] = useState('')
  const [memo, setMemo] = useState('')

  const [preview, setPreview] = useState<CompleteRefundResponse | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)

  const invalidate = useInvalidateRefunds(workspaceId)

  // 다른 행을 열었을 때 앞 행의 입력이 남아 있으면 그 값으로 확정된다
  useEffect(() => {
    if (!open || !row) return
    setRefundedAt(dayjs())
    setChannel(row.channel ?? undefined)
    // 산정액을 채워 두되 고칠 수 있게 둔다 — 결제대행사에서 깎여 나가는 경우가 있고,
    // 그 차이(`amountMismatch`)가 대장에서 신호가 된다
    setActualCancelAmount(row.actualCancelAmount ?? row.amount)
    setPgTransactionNo(row.pgTransactionNo ?? '')
    setMemo('')
    // 🔴 의존성은 `row` 객체가 아니라 **id** 다. 대장이 백그라운드로 다시 불리면 `row` 는 매번
    // 새 객체가 되고, 그때마다 이 효과가 담당자가 입력하던 폼을 되돌린다. 값은 초기화에만
    // 쓰이므로 id 가 같으면 다시 읽을 이유가 없다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row?.refundId])

  /**
   * 폼이 바뀌면 미리보기를 버린다.
   *
   * 낡은 미리보기로 확정 버튼을 열어두면, 담당자가 **본 적 없는 조합**이 실행된다.
   * 금액을 고친 뒤 "아까 봤으니 됐다" 하고 누르는 것이 정확히 이 사고다.
   */
  useEffect(() => {
    setPreview(null)
    setPreviewError(null)
  }, [refundedAt, channel, actualCancelAmount, pgTransactionNo, memo])

  // 같은 API 를 두 번 선언한 것은 **로딩 상태를 나눠야** 하기 때문이다. 하나로 합치면
  // 미리보기를 누른 동안 확정 버튼까지 로딩으로 보여 어느 쪽이 돌고 있는지 알 수 없다.
  const { mutateAsync: runPreview, isPending: previewing } = useMutation({
    mutationFn: (body: CompleteRefundRequest) => completeRefund(row!.refundId, body),
  })
  const { mutateAsync: runComplete, isPending: completing } = useMutation({
    mutationFn: (body: CompleteRefundRequest) => completeRefund(row!.refundId, body),
  })
  const { mutateAsync: runRetry, isPending: retrying } = useMutation({
    mutationFn: () => retryRefundFollowUp(row!.refundId),
  })

  const formReady = !!refundedAt && !!channel && actualCancelAmount != null && actualCancelAmount > 0

  const buildBody = (dryRun: boolean): CompleteRefundRequest => ({
    refundedAt: toLocalDateTime(refundedAt!),
    channel: channel!,
    actualCancelAmount: actualCancelAmount!,
    pgTransactionNo: pgTransactionNo.trim() || undefined,
    memo: memo.trim() || undefined,
    dryRun,
  })

  const handlePreview = async () => {
    setPreviewError(null)
    try {
      setPreview(await runPreview(buildBody(true)))
    } catch (e) {
      setPreview(null)
      setPreviewError(apiErrorMessage(e) ?? NO_REASON)
    }
  }

  /**
   * 확정과 후속 조치는 **트랜잭션이 갈려 있다**(`RefundCompletionService`). 조치가 끊겨도 확정은
   * 남는다 — 성공 토스트 하나로 덮으면 담당자는 구독이 끊긴 줄 알고 넘어가고, 재시도 대상인
   * 행이 대장에 방치된다.
   */
  const announce = (result: CompleteRefundResponse, done: string): FollowUpOutcome => {
    const { followUp } = result

    if (followUp && followUp.action === 'PENDING_GROUP') {
      message.info(`${done} 같은 사건의 다른 행이 확정돼야 후속 조치가 실행됩니다.`)
      return 'PENDING'
    }
    if (followUp && followUp.action !== 'NONE' && !followUp.executed) {
      message.warning(`${done} 다만 후속 조치가 실행되지 않았습니다 — 아래 재시도로 다시 돌리세요.`)
      return 'FAILED'
    }
    message.success(done)
    return 'OK'
  }

  const submit = async () => {
    let result: CompleteRefundResponse
    try {
      result = await runComplete(buildBody(false))
    } catch (e) {
      message.error(apiErrorMessage(e) ?? NO_REASON)
      return
    }

    const outcome = announce(result, '처리 완료로 확정했습니다.')
    await invalidate()

    // 🔴 조치가 실패했으면 서랍을 닫지 않는다. "아래 재시도로 다시 돌리세요" 라고 안내해 놓고
    // 그 버튼이 있는 화면을 같이 닫아버리면, 담당자는 대장에서 그 행을 다시 찾아야 한다.
    // 무효화가 끝난 뒤라 재시도 버튼은 이 자리에 이미 떠 있다.
    if (outcome !== 'FAILED') onClose()
  }

  const confirmAndSubmit = () => {
    Modal.confirm({
      title: '처리 완료로 확정할까요?',
      width: 520,
      content: (
        <Space direction="vertical">
          <span>확정은 되돌릴 수 없습니다. 아래 조치가 실행됩니다.</span>
          <FollowUpSummary followUp={preview?.followUp ?? null} />
        </Space>
      ),
      okText: '확정',
      okButtonProps: { danger: true },
      cancelText: '취소',
      onOk: submit,
    })
  }

  const handleRetry = async () => {
    try {
      announce(await runRetry(), '후속 조치를 다시 실행했습니다.')
    } catch (e) {
      message.error(apiErrorMessage(e) ?? '후속 조치를 재시도하지 못했습니다. (서버가 사유를 주지 않았습니다)')
      return
    }
    await invalidate()
  }

  const target = row ? refundTargetOfRow(row) : null
  const creditPack = target?.kind === 'PAYMENT_ORDER'

  /**
   * 처리일 하한은 결제일이다 — 크레딧 팩 결제 건도 같다(그 결제 건의 결제일, 서버가 검사한다).
   * 구독 종료 유형에서는 처리일이 **비활성 진입일**이 되므로 결제 전에 구독이 끝난 것으로
   * 기록되면 안 된다. 다만 서버는 `invoice.paidAt`(시각)과 비교하고 대장이 주는
   * `invoicePaidOn` 은 날짜뿐이라, 결제 당일의 이른 시각은 여기서 못 거른다 — 미리보기가 잡는다.
   */
  const paidOn = row?.invoicePaidOn ? dayjs(row.invoicePaidOn) : null
  const disabledDate = (current: Dayjs) =>
    current.isAfter(dayjs(), 'day') || (!!paidOn && current.isBefore(paidOn, 'day'))

  const copy = confirmCopy(row?.reason ?? 'OTHER', target)
  const completable = !!row && canCompleteRefund(row.status)
  const retryable = !!row && canRetryFollowUp(row.status, row.followUpStatus)
  const mismatch = !!row && actualCancelAmount != null && actualCancelAmount !== row.amount

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={640}
      destroyOnClose
      title={
        row ? (
          <Space>
            <span>환불 기록 #{row.refundId}</span>
            <Tag color={REFUND_STATUS_COLOR[row.status]}>{REFUND_STATUS_LABEL[row.status] ?? row.status}</Tag>
          </Space>
        ) : (
          '환불 기록'
        )
      }
    >
      {!row ? null : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {/* ── 0. 정정 연결. 대장은 행을 지우지 않으므로 잘못 기입한 행은 철회해 두고 정정
                 기록이 그것을 가리킨다(화면 명세 8.4). 어느 쪽이 유효한 기록인지 여기서 읽는다 ── */}
          {row.supersedesRefundId ? (
            <Alert
              type="info"
              showIcon
              message={`이 기록은 #${row.supersedesRefundId} 을 정정한 것입니다`}
              description={
                onOpenRefund ? (
                  <Button size="small" onClick={() => onOpenRefund(row.supersedesRefundId!)}>
                    원 기록 #{row.supersedesRefundId} 보기
                  </Button>
                ) : null
              }
            />
          ) : null}
          {supersededByRefundId ? (
            <Alert
              type="warning"
              showIcon
              message={`이 기록은 #${supersededByRefundId} 로 정정됐습니다`}
              description={
                <Space direction="vertical" size={4}>
                  <span>유효한 기록은 정정 행입니다. 이 행의 금액으로 대사하지 마세요.</span>
                  {onOpenRefund ? (
                    <Button size="small" onClick={() => onOpenRefund(supersededByRefundId)}>
                      정정 기록 #{supersededByRefundId} 보기
                    </Button>
                  ) : null}
                </Space>
              }
            />
          ) : null}

          {/* ── 1. 산정 결과 — 읽기 전용이다 ── */}
          <div>
            <Typography.Title level={5}>산정 결과</Typography.Title>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 8 }}
              message="접수된 산정값은 고칠 수 없습니다"
              description="고객에게 안내·동의받은 금액입니다. 다시 계산해야 하면 이 행을 철회하고 새로 접수하세요."
            />
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="환불 유형">{REFUND_REASON_LABEL[row.reason] ?? row.reason}</Descriptions.Item>
              <Descriptions.Item label="접수 일시">{datetime(row.createdAt)}</Descriptions.Item>
              <Descriptions.Item label="산정액">
                <Typography.Text strong>{money(row.amount)}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="부가세 제외">{money(row.amountExcludingVat)}</Descriptions.Item>
              <Descriptions.Item label="위약금">
                {money(row.penalty)}
                {row.penaltyRate != null ? ` (${formatRatio(row.penaltyRate)})` : ''}
              </Descriptions.Item>
              <Descriptions.Item label="접수자">{row.issuerEmail ?? '-'}</Descriptions.Item>
              {/* 🔴 금액과 구간·요율을 같은 것으로 묶어 읽지 않는다 — 같은 0원이라도 근거 조항이
                  갈리고(D-66), 구간은 위약금이 0 인 건에도 남는다 */}
              <Descriptions.Item label="위약금 구간">
                {row.penaltyBracket ? PENALTY_BRACKET_LABEL[row.penaltyBracket] : '-'}
              </Descriptions.Item>
              {/* 구독취소·되감기 행에서 비는 것이 정상이다. 빈 칸을 "빠진 값" 으로 읽으면
                  없는 결함을 쫓게 된다(QA Part 2 회신 2번) */}
              <Descriptions.Item
                label={
                  <Tooltip title="단건 크레딧 환불의 공제 근거입니다(정책 13.4). 구독취소·되감기 행에는 붙지 않는 값이라 비어 있는 것이 정상입니다">
                    사용률
                  </Tooltip>
                }
              >
                {formatRatio(row.usageRatio)}
              </Descriptions.Item>
              <Descriptions.Item label="사건 번호" span={2}>
                {row.refundGroupNo ?? '-'}
                {row.pairSize > 1 ? (
                  <Tag style={{ marginLeft: 8 }}>
                    {row.pairSequence}/{row.pairSize}
                  </Tag>
                ) : null}
                {row.awaitingPair ? <Tag color="gold">짝 대기</Tag> : null}
              </Descriptions.Item>
            </Descriptions>
          </div>

          {/* ── 2. 결제대행사로 들고 갈 값 ── */}
          <div>
            <Typography.Title level={5}>결제대행사 지시 정보</Typography.Title>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="취소할 금액">
                <Typography.Text strong copyable={{ text: String(row.amount) }}>
                  {money(row.amount)}
                </Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="취소 종류">{cancelKindLabel(row.amount, row.invoicePrice) ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="결제 고유번호">
                <Typography.Text copyable={!!row.paymentNumber}>{row.paymentNumber ?? '-'}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="주문번호">
                <Typography.Text copyable={!!row.orderNumber}>{row.orderNumber ?? '-'}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="대상 결제 건">
                {/* 청구서와 결제 건은 번호 체계가 달라 `#23` 만으로는 어느 쪽인지 모른다(D-121) */}
                {refundTargetLabel(target)} · {row.invoicePaidOn ?? '-'} 결제 · {money(row.invoicePrice)}
                {creditPack ? (
                  <Tag color="geekblue" style={{ marginLeft: 8 }}>
                    단건 상점
                  </Tag>
                ) : null}
              </Descriptions.Item>
            </Descriptions>
          </div>

          {/* ── 3. 처리 완료 확정 ── */}
          {completable ? (
            <div>
              <Typography.Title level={5}>처리 완료 확정</Typography.Title>
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 8 }}
                message="결제대행사에서 취소를 실행한 뒤에 확정하세요"
                description={copy.warning}
              />

              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <span>
                  처리일 <Typography.Text type="danger">*</Typography.Text>{' '}
                  <Typography.Text type="secondary">— {copy.refundedAtHint}</Typography.Text>
                </span>
                <DatePicker
                  showTime={{ format: 'HH:mm' }}
                  format="YYYY-MM-DD HH:mm"
                  style={{ width: '100%' }}
                  value={refundedAt}
                  disabledDate={disabledDate}
                  onChange={setRefundedAt}
                />
                {/* 달력이 범위 밖을 고를 수 없게 막기만 하고 왜 막는지는 말하지 않아, 담당자가
                    자기 입력이 잘못된 줄 몰랐다(v3 S8). 막는 동작은 그대로 두고 사유만 붙인다 */}
                <Typography.Text type="secondary">
                  {paidOn ? `${paidOn.format('YYYY-MM-DD')} 결제일부터 오늘까지만 고를 수 있습니다` : '오늘까지만 고를 수 있습니다'}
                  {creditPack || !deactivatesSubscription(row.reason)
                    ? ' — 결제 전이나 미래로는 기록할 수 없습니다'
                    : ' — 결제 전이나 미래로 기록되면 비활성 진입일이 어긋납니다'}
                </Typography.Text>

                <span>
                  처리 경로 <Typography.Text type="danger">*</Typography.Text>
                </span>
                <Select
                  style={{ width: '100%' }}
                  placeholder="돈이 실제로 나간 경로"
                  value={channel}
                  options={channelOptions}
                  onChange={setChannel}
                />

                <span>
                  실제 취소 금액 <Typography.Text type="danger">*</Typography.Text>
                </span>
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  addonAfter="원"
                  value={actualCancelAmount}
                  onChange={setActualCancelAmount}
                />
                {mismatch ? (
                  // 서버가 `amountMismatch` 로 판정해 대장에 신호를 남긴다. 막지 않는다 —
                  // 실제로 갈리는 경우가 있고, 숨기면 그 사실이 사라진다
                  <Alert
                    type="warning"
                    showIcon
                    message={`산정액(${money(row.amount)})과 다릅니다`}
                    description="그대로 확정할 수 있습니다. 대장에 '불일치' 로 표시되니 메모에 이유를 남겨주세요."
                  />
                ) : null}

                <span>결제대행사 거래번호 (선택)</span>
                <Input
                  value={pgTransactionNo}
                  placeholder="계좌 환급처럼 번호가 없는 경로면 비워두세요"
                  onChange={(e) => setPgTransactionNo(e.target.value)}
                />

                <span>메모 (선택)</span>
                <Input.TextArea
                  rows={2}
                  maxLength={500}
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  placeholder="비워두면 접수 때 적은 메모가 그대로 남습니다"
                />

                <Divider style={{ margin: '4px 0' }} />

                <Spin spinning={previewing}>
                  {previewError ? (
                    <Alert type="error" showIcon message="확정할 수 없습니다" description={previewError} />
                  ) : null}

                  {preview ? (
                    <Alert
                      type="success"
                      showIcon
                      message="확정하면 이렇게 됩니다"
                      description={<FollowUpSummary followUp={preview.followUp} />}
                    />
                  ) : (
                    <Alert
                      type="info"
                      showIcon
                      message="먼저 미리보기를 받으세요"
                      description="무엇이 실행되는지 확인하기 전에는 확정할 수 없습니다. 입력을 고치면 미리보기를 다시 받아야 합니다."
                    />
                  )}
                </Spin>

                <Space>
                  <Button onClick={handlePreview} loading={previewing} disabled={!formReady}>
                    미리보기
                  </Button>
                  <Button
                    type="primary"
                    danger
                    loading={completing}
                    disabled={!preview || !formReady}
                    onClick={confirmAndSubmit}
                  >
                    처리 완료 확정
                  </Button>
                </Space>
              </Space>
            </div>
          ) : null}

          {/* ── 4. 후속 조치 ── */}
          <div>
            <Typography.Title level={5}>후속 조치</Typography.Title>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="상태">
                {row.followUpStatus ? (
                  <Tag color={FOLLOW_UP_STATUS_COLOR[row.followUpStatus]}>
                    {followUpStatusLabel(row.followUpStatus, row.paymentOrderId)}
                  </Tag>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="실행 시각">{datetime(row.followUpAt)}</Descriptions.Item>
              <Descriptions.Item label="내역">{row.followUpDetail ?? '-'}</Descriptions.Item>
            </Descriptions>
            {retryable ? (
              <Space direction="vertical" style={{ marginTop: 8 }}>
                <Typography.Text type="secondary">
                  {creditPack
                    ? '이 결제 건에 남은 크레딧을 소멸시킵니다. 소멸이 아직 돌지 않은 건(수동 기록 등)만 다시 돌릴 수 있습니다.'
                    : '실패했거나 짝을 기다리던 조치만 다시 돌릴 수 있습니다. 이미 실행된 것을 다시 돌리면 구독을 두 번 끄거나 복원을 두 번 합니다.'}
                </Typography.Text>
                <Button loading={retrying} onClick={handleRetry}>
                  후속 조치 재시도
                </Button>
              </Space>
            ) : null}
          </div>

          {/* ── 5. 상태 전이. 열 수 있는 조치가 없으면 제목만 남으므로 섹션째 감춘다 ── */}
          {canHoldRefund(row.status) || canWithdrawRefund(row.status) ? (
            <div>
              <Typography.Title level={5}>상태 변경</Typography.Title>
              <RefundStateActions row={row} workspaceId={workspaceId} onDone={onClose} />
            </div>
          ) : null}

          {/* ── 6. 이력 ── */}
          <div>
            <Typography.Title level={5}>처리 이력</Typography.Title>
            <RefundHistory row={row} />
          </div>
        </Space>
      )}
    </Drawer>
  )
}
